import { CoreImageView } from '@core/widgets';
import { ConditionalRender, useApp, useBehavior, useList, useTriggerAction } from '@libs/core';
import { AssetsSvgMap, TContacts, TItem, TRowComponentProps, TWidgetProps } from '@libs/types';
import React, { Activity, useEffect, useRef, useState } from 'react';
import { TConsumerListData, TContactValue } from '../../types/data';
import { RowEnum } from '../../types/enums';
import { TListOption } from '../../types/options';
import { convertContactsToRowComponentProps } from '../../utils/helpers';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import ItemListComponent from '../UI/Row/ItemListComponent';
import { TextCommon } from '../UI/Text/TextCommon';
import InputSearch from './InputSearch';
import ListItems from './ListItems';
import styles from './styles/styles.module.css';
import { SEARCH_ENABLED_ITEMS_COUNT_DEFAULT } from './utils/ListConstant';

// @ts-expect-error
const supported = 'contacts' in navigator && 'ContactsManager' in window && 'select' in navigator.contacts && 'getProperties' in navigator.contacts;
export const ListContacts = ({ widget }: TWidgetProps<TConsumerListData, TListOption>) => {
    const { isHidden } = useBehavior(widget);
    const { setModal, setPopup, setBottomSheet } = useApp();
    const { triggerActionsByType } = useTriggerAction(widget);
    const [props, setProps] = useState<TRowComponentProps[] | []>([]);
    const [contacts, setContacts] = useState<TContacts[] | []>([]);
    const { handleClick } = useList(widget, { triggerActionsByType, setModal, setPopup, setBottomSheet });
    const imageRef = useRef(null);
    const data = widget.data ?? {};
    const options = widget.options ?? {};
    const searchEnabledItemsCount = options.searchEnabledItemsCount ?? SEARCH_ENABLED_ITEMS_COUNT_DEFAULT;
    const rowId = data.cellWidgetId ?? RowEnum.ROW_REGULAR;

    useEffect(() => {
        const ref = imageRef.current;
        return () => {
            if (ref) {
                URL.revokeObjectURL(ref);
            }
        };
    }, []);

    const handleInputClick = () => {
        if (supported) {
            // @ts-expect-error
            navigator.contacts
                .getProperties()
                .then((supportedProperties: string[]) => {
                    if (supportedProperties) {
                        // @ts-expect-error
                        return navigator.contacts.select(supportedProperties, { multiple: true });
                    }
                })
                .then((contacts: TContacts[]) => {
                    if (contacts && contacts?.length > 0) {
                        setProps(convertContactsToRowComponentProps(contacts, rowId, imageRef));
                        setContacts(contacts);
                    }
                })
                .catch((e: unknown) => console.warn(e));
        }
    };
    const handleRowClick = (index: number) => {
        const contact: TContactValue = {
            email: contacts[index].email[0],
            fullName: contacts[index].name[0],
            number: contacts[index].tel[0],
        };
        const item: TItem = {
            id: contact.number ?? '',
            title: contact.fullName ?? '',
            value: contact,
        };
        const blob = contacts[index].icon[0];
        if (blob && blob instanceof Blob) {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = function () {
                contact.avatar = reader.result as string;
                handleClick(widget?.uid, item);
            };
            reader.onerror = function () {
                console.error(reader.error);
            };
        } else {
            handleClick(widget?.uid, item);
        }
    };
    const renderItem = ({ item, index }: { item: TRowComponentProps; index: number }) => {
        return <ItemListComponent {...item} key={index} handleClick={() => handleRowClick(index)} />;
    };

    return (
        <Activity mode={isHidden || !supported ? 'hidden' : 'visible'}>
            <div data-test={widget.uid} style={getInlineStyles(options)}>
                <ConditionalRender condition={props.length >= searchEnabledItemsCount}>
                    <InputSearch
                        asButton={true}
                        widget={widget}
                        handleClick={handleInputClick}
                        marginTop={16}
                        marginRight={24}
                        marginBottom={16}
                        marginLeft={24}
                    />
                </ConditionalRender>
                <ConditionalRender condition={props.length > 0}>
                    <ListItems items={props} renderItem={renderItem} />
                </ConditionalRender>
                <ConditionalRender condition={props.length === 0}>
                    <div className={styles.emptyContainer}>
                        <CoreImageView
                            src={getHref(AssetsSvgMap.ic_dotted_list)}
                            styleProps={{
                                iconColor: `var(--grey_72_lc)`,
                            }}
                        />
                        <TextCommon
                            isWidget={false}
                            content={options.emptyContactsResultLabel}
                            options={{ variant: 'secondary', textColor: 'grey_72_lc', margin: { top: 16 } }}
                        />
                    </div>
                </ConditionalRender>
            </div>
        </Activity>
    );
};
